import { apiClient } from "@api/client";
import { request } from "@api/request";
import type { components } from "@api/schema";

/** How Brian handles PIMS writes for a clinic (BE-1815). */
export type PimsWriteMode = components["schemas"]["PimsWriteMode"];

/** Clinic identity + config for the active session, shaped from `GET /auth/clinic`. */
export interface ClinicInfo {
  uuid: string;
  name: string;
  subdomain: string;
  email: string;
  timezone: string;
  pimsWriteMode: PimsWriteMode;
  // The `batch` flush cadence in minutes (BE-1831), or null when the clinic
  // has no flush recurrence (i.e. it isn't in batch mode).
  flushIntervalMinutes: number | null;
  // The clinic's current agent transcript epoch (BE managed-agents removal),
  // shown read-only in admin settings for diagnostics. Null until the clinic's
  // first agent message creates the session row; bumps when the runner rotates
  // the context.
  agentTranscriptEpoch: number | null;
  // Branding (BE-1919). Mailing address and a presigned GET for the stored
  // logo; null until the clinic uploads/sets them.
  address: string | null;
  logoUrl: string | null;
}

const toClinicInfo = (c: components["schemas"]["ClinicOut"]): ClinicInfo => ({
  uuid: c.uuid,
  name: c.name,
  subdomain: c.subdomain,
  email: c.email,
  timezone: c.timezone,
  pimsWriteMode: c.pims_write_mode,
  flushIntervalMinutes: c.flush_interval_minutes ?? null,
  agentTranscriptEpoch: c.agent_transcript_epoch ?? null,
  address: c.address ?? null,
  logoUrl: c.logo_url ?? null,
});

export interface ClinicClient {
  get(): Promise<ClinicInfo>;
  setPimsWriteMode(mode: PimsWriteMode): Promise<ClinicInfo>;
  setFlushInterval(minutes: number): Promise<ClinicInfo>;
}

export const clinicClient: ClinicClient = {
  get: () =>
    request(() => apiClient.GET("/auth/clinic", {})).then(toClinicInfo),

  // Updates the caller's own clinic (no path param — the token carries it).
  // Any authenticated clinic session may set it; a bad mode 422s.
  setPimsWriteMode: (mode) =>
    request(() =>
      apiClient.PATCH("/auth/clinic", { body: { pims_write_mode: mode } }),
    ).then(toClinicInfo),

  // Retune the batch flush cadence. 409s if the clinic isn't in batch mode
  // (no flush recurrence to update), so the control is only shown in batch.
  setFlushInterval: (minutes) =>
    request(() =>
      apiClient.PATCH("/auth/clinic/flush-interval", {
        body: { flush_interval_minutes: minutes },
      }),
    ).then(toClinicInfo),
};
